/* Phase 4 + S21 v3 · jp-product.js — PDP enhancements on top of Dawn machinery.
   Gallery arrows + stage tone (media alt #dark), qty stepper, size-guide
   dialog, sticky buy bar, share, ARIA tabs, cost-per-cut teaser (Intl),
   ATC live price, compact state mirror. Persona reactions (tuned note,
   rec chips, hints, pick badge) are pure CSS off body.persona-* — no JS.
   All binds idempotent (data flags) + re-run on shopify:section:load. */
(() => {
  if (window.__jpProductInit) return;
  window.__jpProductInit = true;

  const ready = (fn) =>
    document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn);

  /* ---- money: format minor-unit cents via Intl (pattern: jp-cost-per-cut).
          Currency default digits respected; only force 3 when sub-minor-unit. ---- */
  const fmtMoney = (cents, currency) => {
    const lang = document.documentElement.lang || 'en';
    const value = cents / 100;
    const opts = { style: 'currency', currency };
    if (value > 0 && value < 0.01) { opts.minimumFractionDigits = 3; opts.maximumFractionDigits = 3; }
    try {
      return new Intl.NumberFormat(lang, opts).format(value);
    } catch { return value.toFixed(2) + ' ' + currency; }
  };

  /* ---- cost-per-cut teaser: cents ÷ (cuts × days × years).
          Server renders it [hidden]; reveal only once a real figure exists
          (Dawn 15 has no no-js class — this is the no-JS guard). ---- */
  const renderPercut = () => {
    document.querySelectorAll('[data-jp-percut]').forEach((el) => {
      const out = el.querySelector('[data-jp-percut-val]');
      if (!out) return;
      const cents = parseFloat(el.dataset.priceCents);
      const cuts = (parseFloat(el.dataset.cuts) || 8) * (parseFloat(el.dataset.days) || 312) * (parseFloat(el.dataset.years) || 10);
      if (!cents || !cuts || !el.dataset.currency) return;
      out.textContent = fmtMoney(cents / cuts, el.dataset.currency);
      el.hidden = false;
    });
  };

  /* ---- ATC label: Kit/Reserve swap + optional live price suffix ---- */
  const atcLabel = () => {
    const atcBtn = document.querySelector('[data-jp-atc]');
    if (!atcBtn) return;
    const span = atcBtn.querySelector('span');
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    const priceNow = () => document.querySelector('[id^="price-"] .jp-pp__now');
    const withPrice = (label) => {
      if (!atcBtn.hasAttribute('data-jp-atc-price')) return label;
      const now = priceNow();
      return now ? label + ' — ' + now.textContent.trim() : label;
    };
    const current = () => {
      const pre = stockBlock && stockBlock.querySelector('.jp-pp__stock--pre');
      return withPrice(pre ? atcBtn.dataset.labelReserve : atcBtn.dataset.labelKit);
    };
    const sync = () => {
      /* Dawn's toggleSubmitButton writes window.variantStrings.addToCart on
         enable — keep that global equal to our full label so Dawn writes
         the brand text (incl. price) itself. */
      const label = current();
      if (window.variantStrings) window.variantStrings.addToCart = label;
      if (span && !atcBtn.hasAttribute('disabled')) span.textContent = label;
    };
    sync();
    if (stockBlock && !stockBlock.dataset.jpAtcBound) {
      stockBlock.dataset.jpAtcBound = '1';
      new MutationObserver(sync).observe(stockBlock, { childList: true, subtree: true });
    }
    return sync;
  };

  /* ---- compact state mirror in the rate row (Inventory- block is the
          swap target; the mirror echoes it after variant changes) ---- */
  const stateMirror = () => {
    const mirror = document.querySelector('[data-jp-state-mirror]');
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    if (!mirror || !stockBlock) return;
    const sync = () => {
      const line = stockBlock.querySelector('.jp-pp__stock-line');
      if (!line) return;
      const key = line.classList.contains('jp-pp__stock--in') ? 'in'
        : line.classList.contains('jp-pp__stock--pre') ? 'pre' : 'out';
      mirror.className = 'jp-pp__state jp-pp__state--' + key;
      const t = mirror.querySelector('.t');
      if (t) t.textContent = line.textContent.replace(/\s+/g, ' ').trim();
    };
    if (!stockBlock.dataset.jpMirrorBound) {
      stockBlock.dataset.jpMirrorBound = '1';
      new MutationObserver(sync).observe(stockBlock, { childList: true, subtree: true });
    }
  };

  const init = () => {
    /* ---- gallery prev/next + stage tone ---- */
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      /* CRIT-2: Dawn media-gallery.setActiveThumbnail() calls thumbnails.isSlideVisible()
         and .slider — slider-component members our plain-<div> thumbnail rail lacks, so
         Dawn throws on every thumb click / variant media change. Give the rail a no-op
         isSlideVisible() so Dawn sets aria-current then early-returns (the rail doesn't
         horizontally scroll). Idempotent; never edits the Dawn file. */
      const jpThumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (jpThumbs && typeof jpThumbs.isSlideVisible !== 'function') jpThumbs.isSlideVisible = () => true;
      const lis = () => [...gallery.querySelectorAll('.jp-pp__viewer li')];
      const stage = gallery.querySelector('[data-jp-stage]');
      const syncTone = () => {
        if (!stage) return;
        const active = lis().find((li) => li.classList.contains('is-active'));
        if (!active || !active.dataset.jpTone) return;
        const dark = active.dataset.jpTone === 'dark';
        if (stage.classList.contains('is-dark') === dark) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          stage.classList.toggle('is-dark', dark);
          return;
        }
        stage.classList.add('is-swapping');
        setTimeout(() => {
          stage.classList.toggle('is-dark', dark);
          stage.classList.remove('is-swapping');
        }, 160);
      };
      if (!gallery.dataset.jpGalBound) {
        gallery.dataset.jpGalBound = '1';
        const step = (dir) => {
          const items = lis();
          const current = Math.max(0, items.findIndex((li) => li.classList.contains('is-active')));
          const next = (current + dir + items.length) % items.length;
          gallery.setActiveMedia(items[next].dataset.mediaId, false);
        };
        gallery.querySelector('[data-jp-gallery-prev]')?.addEventListener('click', () => step(-1));
        gallery.querySelector('[data-jp-gallery-next]')?.addEventListener('click', () => step(1));
        /* media-gallery toggles is-active on the lis (thumb clicks + variant
           media changes) — observe class flips, never patch Dawn */
        const viewer = gallery.querySelector('.jp-pp__viewer');
        if (viewer) new MutationObserver(syncTone).observe(viewer, { attributes: true, attributeFilter: ['class'], subtree: true });
        syncTone();
      }
    });

    /* ---- qty stepper (document-level, bind once) ---- */
    if (!document.body.dataset.jpQtyBound) {
      document.body.dataset.jpQtyBound = '1';
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-jp-qty]');
        if (!btn) return;
        const input = btn.closest('.jp-pp__qty')?.querySelector('input');
        if (!input) return;
        const next = Math.max(parseInt(input.min || 1, 10), (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.jpQty, 10));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /* ---- size guide dialog ---- */
    const guide = document.querySelector('[data-jp-guide]');
    if (guide && !guide.dataset.jpBound) {
      guide.dataset.jpBound = '1';
      let lastFocus = null;
      const open = () => {
        lastFocus = document.activeElement;
        guide.hidden = false;
        requestAnimationFrame(() => guide.classList.add('is-open'));
        guide.querySelector('.jp-guide__close')?.focus();
        document.body.style.overflow = 'hidden';
      };
      const close = () => {
        guide.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(() => { guide.hidden = true; }, 300);
        lastFocus?.focus();
      };
      document.querySelectorAll('[data-jp-guide-open]').forEach((b) => b.addEventListener('click', open));
      guide.querySelectorAll('[data-jp-guide-close]').forEach((b) => b.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !guide.hidden) close();
      });
      /* Tab focus trap — the dialog sets aria-modal="true"; keep focus inside it */
      guide.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || guide.hidden) return;
        const f = [...guide.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    /* ---- image zoom / lightbox (premium close-up for blade/finish detail) ---- */
    if (!document.body.dataset.jpZoomBound) {
      document.body.dataset.jpZoomBound = '1';
      let zoomReturn = null;
      const z = document.createElement('div');
      z.className = 'jp-zoom';
      z.setAttribute('role', 'dialog');
      z.setAttribute('aria-modal', 'true');
      z.hidden = true;
      z.innerHTML =
        '<div class="jp-zoom__scrim" data-jp-zoom-close></div>' +
        '<button type="button" class="jp-zoom__close" data-jp-zoom-close><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"></path></svg></button>' +
        '<img class="jp-zoom__img" alt="">';
      document.body.appendChild(z);
      // localized close label (en/fr/it) carried on the stage element, falls back to English
      const jpStageEl = document.querySelector('[data-jp-stage]');
      z.querySelector('.jp-zoom__close').setAttribute('aria-label', (jpStageEl && jpStageEl.dataset.closeLabel) || 'Close');
      const zImg = z.querySelector('.jp-zoom__img');
      const closeZoom = () => {
        if (z.hidden) return;
        z.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(() => { z.hidden = true; zImg.removeAttribute('src'); }, 250);
        if (zoomReturn) zoomReturn.focus();
      };
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-jp-stage] .jp-pp__viewer li.is-active img');
        if (trigger && z.hidden) {
          zoomReturn = document.activeElement;
          zImg.src = trigger.currentSrc || trigger.src;
          zImg.alt = trigger.alt || '';
          z.hidden = false;
          requestAnimationFrame(() => z.classList.add('is-open'));
          document.body.style.overflow = 'hidden';
          z.querySelector('.jp-zoom__close').focus();
          return;
        }
        if (!z.hidden && (e.target === zImg || e.target.closest('[data-jp-zoom-close]'))) closeZoom();
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeZoom(); });
    }

    /* ---- ATC label + state mirror + percut (independent of sticky) ---- */
    atcLabel();
    stateMirror();
    renderPercut();

    /* ---- shared price-block observer: sticky price + percut + ATC price ---- */
    const priceBlock = document.querySelector('[id^="price-"]');
    if (priceBlock && !priceBlock.dataset.jpPriceBound) {
      priceBlock.dataset.jpPriceBound = '1';
      new MutationObserver(() => {
        renderPercut();
        const sync = atcLabel();
        if (sync) sync();
        const now = priceBlock.querySelector('.jp-pp__now');
        const stickyPrice = document.querySelector('[data-jp-sticky] .jp-sticky__t > span');
        if (now && stickyPrice) {
          const note = stickyPrice.querySelector('small');
          stickyPrice.textContent = now.textContent.trim() + (note ? ' ' : '');
          if (note) stickyPrice.appendChild(note);
        }
        /* keep the sticky ATC in step with the real button (label + sold-out/disabled) */
        const mainAtc = document.querySelector('[id^="ProductSubmitButton-"]');
        const stickyAtc = document.querySelector('[data-jp-sticky-atc]');
        if (mainAtc && stickyAtc) {
          stickyAtc.disabled = mainAtc.disabled;
          const ml = mainAtc.querySelector('span');
          if (ml) stickyAtc.textContent = ml.textContent.trim();
        }
      }).observe(priceBlock, { childList: true, subtree: true });
    }

    /* ---- sticky buy bar ---- */
    const sticky = document.querySelector('[data-jp-sticky]');
    const panel = document.querySelector('[data-jp-buy-panel]');
    if (sticky && panel && !sticky.dataset.jpBound && 'IntersectionObserver' in window) {
      sticky.dataset.jpBound = '1';
      new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        },
        { threshold: 0 }
      ).observe(panel);
      sticky.querySelector('[data-jp-sticky-atc]')?.addEventListener('click', () => {
        document.querySelector('[id^="ProductSubmitButton-"]')?.click();
      });
    }

    /* ---- share ---- */
    document.querySelectorAll('[data-jp-share]').forEach((btn) => {
      if (btn.dataset.jpBound) return;
      btn.dataset.jpBound = '1';
      btn.addEventListener('click', async () => {
        const url = btn.dataset.shareUrl || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url });
          } else {
            await navigator.clipboard.writeText(url);
            const original = btn.innerHTML;
            btn.textContent = btn.dataset.copiedText || 'Link copied';
            setTimeout(() => { btn.innerHTML = original; }, 1600);
          }
        } catch {}
      });
    });

    /* ---- product tabs (ARIA tabs w/ arrow keys + #jp-reviews deep link) ---- */
    document.querySelectorAll('[data-jp-ptabs]').forEach((root) => {
      if (root.dataset.jpBound) return;
      root.dataset.jpBound = '1';
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const panels = [...root.querySelectorAll('[role="tabpanel"]')];
      const select = (tab, focus = true) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1, Home: 0, End: 0 }[e.key];
          if (dir === undefined) return;
          e.preventDefault();
          if (e.key === 'Home') return select(tabs[0]);
          if (e.key === 'End') return select(tabs[tabs.length - 1]);
          select(tabs[(i + dir + tabs.length) % tabs.length]);
        });
      });
      const reviewsTab = tabs.find((t) => t.dataset.jpTab === 'reviews');
      if (reviewsTab) {
        const openReviews = () => {
          select(reviewsTab, false);
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        if (window.location.hash === '#jp-reviews') openReviews();
        document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
          if (a.dataset.jpBound) return;
          a.dataset.jpBound = '1';
          a.addEventListener('click', (e) => { e.preventDefault(); openReviews(); });
        });
      }
    });

    /* #jp-reviews target may be a standalone section (P4c.3) — smooth-scroll links */
    const reviewsSection = document.getElementById('jp-reviews');
    if (reviewsSection) {
      document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
        if (a.dataset.jpBound) return;
        a.dataset.jpBound = '1';
        a.addEventListener('click', (e) => {
          e.preventDefault();
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }
  };

  ready(init);
  document.addEventListener('shopify:section:load', () => init());
})();
